import { useState } from "react";
import { MapPin, Phone, Mail, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "../lib/api";
import { company } from "../lib/data";
import useSEO from "../lib/seo";

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

const inputCls =
  "w-full bg-[#1a1a1a] border border-white/10 text-white placeholder-neutral-500 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#dc2626] focus:ring-1 focus:ring-[#dc2626] transition-colors";
const labelCls =
  "block text-xs font-bold tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono";

export default function Contact() {
  useSEO({
    title: "Contact Us — DVP DIGI WORLD, Kolathur, Chennai",
    description:
      "Speak to DVP DIGI WORLD's team for personal, business or corporate financial requirements. Call 9092622438 or visit our office at No.10 Venkateswara Nagar, Perambur Paper Mills Road, Kolathur, Chennai 600099.",
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "DVP DIGI WORLD",
      "image":
        "https://customer-assets.emergentagent.com/job_unified-finance-13/artifacts/fxukgoth_image.png",
      "telephone": "+91-9092622438",
      "email": "contact@dvpdigiworld.com",
      "url": "https://dvpdigi.world/contact",
      "priceRange": "₹₹",
      "address": {
        "@type": "PostalAddress",
        "streetAddress":
          "No.10 Venkateswara Nagar, Perambur Paper Mills Road, Kolathur",
        "addressLocality": "Chennai",
        "addressRegion": "Tamil Nadu",
        "postalCode": "600099",
        "addressCountry": "IN",
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "13.1234",
        "longitude": "80.2191",
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ],
        "opens": "09:30",
        "closes": "19:00",
      },
    },
  });
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Thanks — we've received your message.");
      setForm(initial);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't send. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      {/* HERO */}
      <section className="relative border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1d4ed8]/15 via-transparent to-[#dc2626]/15" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#dc2626]">
            Contact Us
          </div>
          <h1 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-6xl tracking-tighter text-white max-w-3xl leading-[0.95]">
            Speak to our team.
          </h1>
          <p className="mt-6 text-lg text-neutral-300 max-w-2xl leading-relaxed">
            Find the right financial solution for your personal, business or
            corporate requirement. Our team is ready to help.
          </p>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <div className="border border-white/10 rounded-xl bg-[#121212] p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-lg bg-[#dc2626]/10 border border-[#dc2626]/20 flex items-center justify-center text-[#dc2626]">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">
                    Office
                  </div>
                  <p className="text-white leading-relaxed" data-testid="contact-address">
                    {company.address}
                  </p>
                </div>
              </div>
            </div>

            <div className="border border-white/10 rounded-xl bg-[#121212] p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-lg bg-[#dc2626]/10 border border-[#dc2626]/20 flex items-center justify-center text-[#dc2626]">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">
                    Phone
                  </div>
                  <a
                    href={company.phoneHref}
                    data-testid="contact-phone"
                    className="text-white font-mono text-xl hover:text-[#dc2626] transition-colors block"
                  >
                    {company.phone}
                  </a>
                  <a
                    href={company.phoneAltHref}
                    data-testid="contact-phone-alt"
                    className="text-neutral-300 font-mono text-base hover:text-[#dc2626] transition-colors block mt-1"
                  >
                    {company.phoneAlt}
                  </a>
                </div>
              </div>
            </div>

            <div className="border border-white/10 rounded-xl bg-[#121212] p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-lg bg-[#dc2626]/10 border border-[#dc2626]/20 flex items-center justify-center text-[#dc2626]">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">
                    Email
                  </div>
                  <a
                    href={`mailto:${company.email}`}
                    className="text-white hover:text-[#dc2626] transition-colors"
                    data-testid="contact-email"
                  >
                    {company.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden border border-white/10">
              <iframe
                title="DVP DIGI WORLD Location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(company.mapsQuery)}&output=embed`}
                className="w-full h-64 grayscale contrast-125"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="border border-white/10 rounded-2xl bg-[#0f0f0f] p-5 sm:p-10 space-y-6"
            >
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-white tracking-tighter">
                Send us a message
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Name *</label>
                  <input
                    className={inputCls}
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Your name"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <input
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@example.com"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="10-digit mobile"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div>
                  <label className={labelCls}>Subject</label>
                  <input
                    className={inputCls}
                    value={form.subject}
                    onChange={(e) => update("subject", e.target.value)}
                    placeholder="e.g. Business loan enquiry"
                    data-testid="contact-input-subject"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Message *</label>
                  <textarea
                    rows={5}
                    className={inputCls}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="How can we help?"
                    data-testid="contact-input-message"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] disabled:opacity-70 text-white px-6 py-4 font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send Message <ArrowUpRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
